import { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── NAV DATA ────────────────────────────────────────────────────────────────
const navItems = [
  { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",      path: "/dashboard"     }] },
  { section: "ENGAGE",    items: [
    { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
    { icon: "ti-users",        label: "Leads",         path: "/leads"         },
  ]},
  { section: "CONFIGURE", items: [
    { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
    { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
    { icon: "ti-settings", label: "Settings",       path: "/settings"  },
  ]},
];

// ─── DESKTOP SIDEBAR ─────────────────────────────────────────────────────────
function Sidebar() {
  const navigate = useNavigate();
  return (
    <aside className="hidden md:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}>
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>
      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => (
              <div key={label} onClick={() => navigate(path)}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all ${
                  path === "/leads"
                    ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                    : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                }`}>
                <i className={`ti ${icon} text-base flex-shrink-0`} />
                <span className="text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" /> Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">© 2026 AWK Technologies</div>
      </div>
    </aside>
  );
}

// ─── MOBILE SLIDE-IN MENU (AWK-style) ────────────────────────────────────────
function MobileMenu({ open, onClose }) {
  const navigate = useNavigate();
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 md:hidden ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(2px)" }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="fixed top-0 left-0 h-full z-50 flex flex-col transition-transform duration-300 ease-out md:hidden"
        style={{
          width: 300, background: "#161412", borderRight: "1px solid #2a2825",
          transform: open ? "translateX(0)" : "translateX(-100%)",
        }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500 hover:text-stone-200 hover:bg-white/5 transition-all">
            <i className="ti ti-x text-lg" />
          </button>
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {navItems.map(({ section, items }) => (
            <div key={section} className="mb-5">
              <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-1">{section}</div>
              {items.map(({ icon, label, path }) => (
                <button key={label} onClick={() => { navigate(path); onClose(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-left transition-all active:scale-[0.98] ${
                    path === "/leads"
                      ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                      : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                  }`}>
                  <i className={`ti ${icon} text-base flex-shrink-0`} />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full border-2 border-orange-600"
              style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)" }} />
            <div>
              <div className="text-sm font-semibold text-stone-200">Ahmad</div>
              <div className="text-[10px] text-stone-600">AWK Technologies</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-700">
            <i className="ti ti-shield-check text-orange-700/50" /> Secured workspace · © 2026 AWK Technologies
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MOBILE TOPBAR ───────────────────────────────────────────────────────────
function MobileTopbar({ onMenuOpen, onShowModal, onLogout }) {
  return (
    <header className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0 md:hidden"
      style={{ background: "#161412", borderColor: "#2a2825",
        paddingTop: "max(12px, env(safe-area-inset-top, 12px))" }}>
      <button onClick={onMenuOpen}
        className="w-9 h-9 rounded-xl flex items-center justify-center text-stone-400 hover:text-white flex-shrink-0 transition-all"
        style={{ border: "1px solid #2a2825" }}>
        <i className="ti ti-menu-2 text-lg" />
      </button>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="w-6 h-6 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-xs" />
        </div>
        <span className="text-sm font-bold text-stone-100">Leads</span>
        <div className="flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: "#e8602a18", border: "1px solid #e8602a30" }}>
          <i className="ti ti-users text-orange-500 text-[10px]" />
          <span className="text-[10px] font-semibold text-orange-400">248</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button onClick={onShowModal}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all active:scale-95"
          style={{ background: "#e8602a" }}>
          <i className="ti ti-plus text-base" />
        </button>
        <button onClick={onLogout}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-stone-400"
          style={{ border: "1px solid #2a2825" }}>
          <i className="ti ti-power text-sm" />
        </button>
      </div>
    </header>
  );
}

// ─── DESKTOP TOPBAR ──────────────────────────────────────────────────────────
function DesktopTopbar({ onLogout }) {
  const [showLogout, setShowLogout] = useState(false);
  return (
    <header className="hidden md:flex items-center gap-4 px-6 py-3 border-b flex-shrink-0"
      style={{ background: "#161412", borderColor: "#2a2825" }}>
      <div className="flex gap-1.5">
        {["ti-moon-stars","ti-sun"].map(ic => (
          <button key={ic} className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-300"
            style={{ background: "#242220" }}>
            <i className={`ti ${ic} text-xs`} />
          </button>
        ))}
      </div>
      <div className="flex-1 flex items-center gap-2 rounded-full px-4 py-2 border mx-4"
        style={{ background: "#1e1c1a", borderColor: "#333" }}>
        <i className="ti ti-search text-stone-600 text-xs" />
        <span className="text-[11px] text-stone-600">Search leads…</span>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <button onMouseEnter={() => setShowLogout(true)} onMouseLeave={() => setShowLogout(false)}
            onClick={onLogout}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all"
            style={{ background: "#e8602a" }}>
            <i className="ti ti-power text-lg" />
          </button>
          {showLogout && (
            <div onMouseEnter={() => setShowLogout(true)} onMouseLeave={() => setShowLogout(false)}
              className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
              style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <button onClick={onLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-300 hover:bg-orange-500/10 hover:text-orange-400 flex items-center gap-2">
                <i className="ti ti-logout text-orange-500" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── DATA ────────────────────────────────────────────────────────────────────
const statusConfig = {
  New:       { color: "#5b8dee", bg: "#1e2d4a" },
  Contacted: { color: "#f59e0b", bg: "#2d2010" },
  Qualified: { color: "#34c97c", bg: "#0d2d1e" },
  Proposal:  { color: "#a855f7", bg: "#1e1030" },
  Closed:    { color: "#e8602a", bg: "#2d1810" },
  Lost:      { color: "#6b7280", bg: "#1a1a1a" },
};
const sourceConfig = {
  "Web Chat":  { icon: "ti-message-dots",   color: "#5b8dee" },
  "WhatsApp":  { icon: "ti-brand-whatsapp", color: "#34c97c" },
  "Email":     { icon: "ti-mail",           color: "#f59e0b" },
  "LinkedIn":  { icon: "ti-brand-linkedin", color: "#5b8dee" },
  "Referral":  { icon: "ti-users",          color: "#a855f7" },
  "API":       { icon: "ti-code",           color: "#e8602a" },
};
const leads = [
  { id:1,  name:"Sarah Mitchell",   company:"Apex Ventures",     email:"sarah@apexvc.com",       phone:"+1 415 200 1234", status:"Qualified", source:"Web Chat", value:"$8,400",  score:92, avatar:"SM", avatarColor:"#e8602a", tags:["Enterprise","Hot"],   lastContact:"2h ago",    assignee:"Ahmad" },
  { id:2,  name:"James Okafor",     company:"BrightPath Ltd",    email:"james@brightpath.io",    phone:"+44 20 7946 001", status:"New",       source:"LinkedIn", value:"$3,200",  score:61, avatar:"JO", avatarColor:"#5b8dee", tags:["SMB"],                lastContact:"1d ago",    assignee:"Aisha" },
  { id:3,  name:"Lena Hoffmann",    company:"NeuroScale GmbH",   email:"lena.h@neuroscale.de",   phone:"+49 30 1234 567", status:"Proposal",  source:"Email",    value:"$21,000", score:87, avatar:"LH", avatarColor:"#a855f7", tags:["Enterprise","Warm"],  lastContact:"3h ago",    assignee:"Ahmad" },
  { id:4,  name:"Carlos Reyes",     company:"Solara Analytics",  email:"carlos@solara.mx",       phone:"+52 55 1234 890", status:"Contacted", source:"WhatsApp", value:"$5,700",  score:74, avatar:"CR", avatarColor:"#34c97c", tags:["Mid-Market"],          lastContact:"5h ago",    assignee:"Bilal" },
  { id:5,  name:"Priya Nair",       company:"Zenith Capital",    email:"priya@zenithcap.in",     phone:"+91 98765 43210", status:"Closed",    source:"Referral", value:"$14,500", score:98, avatar:"PN", avatarColor:"#f59e0b", tags:["Enterprise","Won"],   lastContact:"Yesterday", assignee:"Ahmad" },
  { id:6,  name:"Tom Bridger",      company:"PeakFlow SaaS",     email:"tom@peakflow.io",        phone:"+1 628 300 5678", status:"Lost",      source:"Web Chat", value:"$2,100",  score:28, avatar:"TB", avatarColor:"#6b7280", tags:["SMB","Cold"],          lastContact:"2d ago",    assignee:"Aisha" },
  { id:7,  name:"Fatima Al-Hassan", company:"Gulf Innovations",  email:"fatima@gulfinno.ae",     phone:"+971 50 123 4567",status:"Qualified", source:"API",      value:"$18,900", score:89, avatar:"FA", avatarColor:"#e8602a", tags:["Enterprise"],          lastContact:"1h ago",    assignee:"Ahmad" },
  { id:8,  name:"Ravi Menon",       company:"DataPilot Inc",     email:"ravi@datapilot.com",     phone:"+1 512 400 7890", status:"New",       source:"LinkedIn", value:"$4,300",  score:55, avatar:"RM", avatarColor:"#5b8dee", tags:["Mid-Market"],          lastContact:"3d ago",    assignee:"Bilal" },
  { id:9,  name:"Elena Vasquez",    company:"CloudRoot Systems", email:"elena@cloudroot.es",     phone:"+34 91 234 5678", status:"Contacted", source:"Email",    value:"$6,800",  score:70, avatar:"EV", avatarColor:"#34c97c", tags:["Warm"],                lastContact:"6h ago",    assignee:"Aisha" },
  { id:10, name:"Marcus Yuen",      company:"Pinnacle Tech",     email:"marcus@pinnacletech.sg", phone:"+65 9123 4567",   status:"Proposal",  source:"Web Chat", value:"$11,200", score:83, avatar:"MY", avatarColor:"#a855f7", tags:["Enterprise","Hot"],   lastContact:"30m ago",   assignee:"Ahmad" },
];
const statCards = [
  { label:"Total Leads",    value:"248",  icon:"ti-users",          color:"#e8602a", delta:"+12%"  },
  { label:"Qualified",      value:"84",   icon:"ti-circle-check",   color:"#34c97c", delta:"+8%"   },
  { label:"Avg Lead Score", value:"73",   icon:"ti-chart-bar",      color:"#5b8dee", delta:"+5pts" },
  { label:"Pipeline Value", value:"$96K", icon:"ti-currency-dollar",color:"#a855f7", delta:"+22%"  },
];
const allStatuses = ["All", ...Object.keys(statusConfig)];
const allSources  = ["All Sources", ...Object.keys(sourceConfig)];

// ─── SCORE BAR ───────────────────────────────────────────────────────────────
function ScoreBar({ score }) {
  const color = score >= 80 ? "#34c97c" : score >= 60 ? "#f59e0b" : "#e8602a";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full" style={{ background: "#252320" }}>
        <div className="h-1.5 rounded-full" style={{ width:`${score}%`, background:color }} />
      </div>
      <span className="text-[10px] font-semibold w-6 text-right" style={{ color }}>{score}</span>
    </div>
  );
}

// ─── MOBILE LEAD CARD ────────────────────────────────────────────────────────
function MobileLeadCard({ lead, onSelect }) {
  const sc = statusConfig[lead.status];
  const src = sourceConfig[lead.source];
  return (
    <div onClick={() => onSelect(lead)}
      className="rounded-2xl border p-4 active:scale-[0.99] transition-all cursor-pointer"
      style={{ background: "#1a1815", borderColor: "#2a2825" }}>
      {/* Top row */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ background: lead.avatarColor }}>
          {lead.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-100 truncate">{lead.name}</div>
              <div className="text-[11px] text-stone-500 truncate">{lead.company}</div>
            </div>
            <span className="text-[10px] font-semibold rounded-full px-2.5 py-1 flex-shrink-0"
              style={{ color: sc.color, background: sc.bg }}>
              {lead.status}
            </span>
          </div>
        </div>
      </div>

      {/* Score bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-stone-600">Lead Score</span>
          <span className="text-[11px] font-bold text-stone-300">{lead.value}</span>
        </div>
        <ScoreBar score={lead.score} />
      </div>

      {/* Bottom row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <i className={`ti ${src.icon} text-xs`} style={{ color: src.color }} />
          <span className="text-[10px] text-stone-500">{lead.source}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-stone-600">{lead.lastContact}</span>
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold"
            style={{ background: lead.avatarColor }}>
            {lead.assignee[0]}
          </div>
        </div>
      </div>

      {/* Tags */}
      {lead.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2.5">
          {lead.tags.map(t => (
            <span key={t} className="text-[9px] text-stone-500 rounded-full px-2 py-0.5" style={{ background: "#252320" }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MOBILE LEAD DETAIL SHEET ────────────────────────────────────────────────
function MobileLeadSheet({ lead, onClose }) {
  const [tab, setTab] = useState("Overview");
  if (!lead) return null;
  const sc = statusConfig[lead.status];
  const src = sourceConfig[lead.source];

  return (
    <>
      <div className="fixed inset-0 z-40 md:hidden" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden rounded-t-3xl flex flex-col overflow-hidden"
        style={{ background: "#161412", border: "1px solid #2a2825", maxHeight: "85vh" }}>
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ background: "#2a2825" }} />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0" style={{ borderColor: "#2a2825" }}>
          <span className="text-sm font-semibold text-stone-200">Lead Profile</span>
          <button onClick={onClose} className="w-7 h-7 rounded-xl flex items-center justify-center text-stone-500"
            style={{ background: "#252320" }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>
        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          <div className="p-5 flex flex-col gap-4">
            {/* Profile */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                style={{ background: lead.avatarColor }}>
                {lead.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-stone-100 truncate">{lead.name}</div>
                <div className="text-[12px] text-stone-500 truncate">{lead.company}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] font-semibold rounded-full px-2.5 py-0.5"
                    style={{ color: sc.color, background: sc.bg }}>{lead.status}</span>
                  <span className="text-[10px] font-bold text-stone-300">{lead.value}</span>
                </div>
              </div>
            </div>

            {/* Score */}
            <div className="rounded-xl p-3 border" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-stone-500">Lead Score</span>
                <i className="ti ti-sparkles text-orange-600 text-xs" />
              </div>
              <ScoreBar score={lead.score} />
            </div>

            {/* Tabs */}
            <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: "#2a2825" }}>
              {["Overview","Activity","Notes"].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`flex-1 py-2.5 text-[11px] font-semibold transition-all ${tab===t ? "text-orange-400" : "text-stone-600"}`}
                  style={tab===t ? { background:"#e8602a18" } : { background:"#1a1815" }}>
                  {t}
                </button>
              ))}
            </div>

            {tab === "Overview" && (
              <div className="flex flex-col gap-3">
                {[
                  { icon:"ti-mail",  label:"Email",    val:lead.email    },
                  { icon:"ti-phone", label:"Phone",    val:lead.phone    },
                  { icon:"ti-currency-dollar", label:"Value", val:lead.value },
                  { icon:"ti-user",  label:"Assignee", val:lead.assignee },
                ].map(({ icon, label, val }) => (
                  <div key={label} className="flex items-center gap-3 rounded-xl px-3 py-2.5 border"
                    style={{ background:"#1a1815", borderColor:"#2a2825" }}>
                    <i className={`ti ${icon} text-orange-600 text-sm flex-shrink-0`} />
                    <div className="min-w-0">
                      <div className="text-[9px] text-stone-600 uppercase tracking-wider">{label}</div>
                      <div className="text-[12px] text-stone-300 truncate">{val}</div>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border"
                  style={{ background:"#1a1815", borderColor:"#2a2825" }}>
                  <i className={`ti ${src.icon} text-sm`} style={{ color:src.color }} />
                  <span className="text-[12px] text-stone-400">via <span className="text-stone-200 font-medium">{lead.source}</span></span>
                  <span className="ml-auto text-[10px] text-stone-600">{lead.lastContact}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {lead.tags.map(t => (
                    <span key={t} className="text-[10px] text-stone-500 rounded-full px-2.5 py-1" style={{ background:"#252320" }}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {tab === "Activity" && (
              <div className="flex flex-col gap-0">
                {[
                  { icon:"ti-message-dots", text:`AI Agent initiated conversation via ${lead.source}`, time:lead.lastContact },
                  { icon:"ti-mail",         text:"Follow-up email sent automatically", time:"1d ago" },
                  { icon:"ti-user-check",   text:`Assigned to ${lead.assignee}`,        time:"2d ago" },
                  { icon:"ti-sparkles",     text:"Lead scored and qualified by Nigella VOX", time:"3d ago" },
                ].map((item, i, arr) => (
                  <div key={i} className="flex gap-3 pb-4 relative">
                    {i < arr.length-1 && <div className="absolute left-[14px] top-7 bottom-0 w-px" style={{ background:"#2a2825" }} />}
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                      style={{ background:"#252320", border:"1px solid #2a2825" }}>
                      <i className={`ti ${item.icon} text-[10px] text-orange-500`} />
                    </div>
                    <div className="pt-0.5 min-w-0">
                      <div className="text-[12px] text-stone-400 leading-snug">{item.text}</div>
                      <div className="text-[10px] text-stone-600 mt-0.5">{item.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "Notes" && (
              <div className="flex flex-col gap-2">
                <textarea rows={4} placeholder="Add a note about this lead…"
                  className="w-full rounded-xl px-3 py-2.5 text-[13px] text-stone-300 placeholder-stone-600 outline-none resize-none border"
                  style={{ background:"#1a1815", borderColor:"#2a2825" }} />
                <button className="w-full py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ background:"#e8602a" }}>Save Note</button>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 pb-6"
              style={{ paddingBottom:"max(24px, env(safe-area-inset-bottom, 24px))" }}>
              <button className="w-full py-3.5 rounded-2xl text-sm font-semibold text-white active:scale-[0.98] transition-all"
                style={{ background:"#e8602a", boxShadow:"0 4px 16px rgba(232,96,42,0.35)" }}>
                <i className="ti ti-message-dots mr-2" /> Start Conversation
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button className="py-3 rounded-xl text-sm font-medium text-stone-400 border active:scale-[0.98]"
                  style={{ background:"#1a1815", borderColor:"#2a2825" }}>
                  <i className="ti ti-mail mr-1.5" /> Send Email
                </button>
                <button className="py-3 rounded-xl text-sm font-medium text-stone-400 border active:scale-[0.98]"
                  style={{ background:"#1a1815", borderColor:"#2a2825" }}>
                  <i className="ti ti-edit mr-1.5" /> Edit Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── ADD LEAD MODAL ──────────────────────────────────────────────────────────
function AddLeadModal({ onClose }) {
  const fields = [
    { label:"Full Name", placeholder:"Sarah Mitchell", icon:"ti-user"     },
    { label:"Company",   placeholder:"Apex Ventures",  icon:"ti-building" },
    { label:"Email",     placeholder:"sarah@co.com",   icon:"ti-mail"     },
    { label:"Phone",     placeholder:"+1 415 000 0000",icon:"ti-phone"    },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ background:"rgba(0,0,0,0.75)" }} onClick={onClose}>
      <div
        className="w-full md:w-auto rounded-t-3xl md:rounded-2xl p-6 border flex flex-col gap-4 md:w-[440px]"
        style={{ background:"#1a1815", borderColor:"#2a2825", maxHeight:"90vh", overflowY:"auto",
          paddingBottom:"max(24px, env(safe-area-inset-bottom, 24px))" }}
        onClick={e => e.stopPropagation()}>
        {/* Handle (mobile) */}
        <div className="flex justify-center -mt-2 mb-1 md:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background:"#2a2825" }} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-stone-100">Add New Lead</div>
            <div className="text-[10px] text-stone-500 mt-0.5">Manually create a new lead record</div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-600"
            style={{ background:"#252320" }}>
            <i className="ti ti-x text-xs" />
          </button>
        </div>
        <div className="flex flex-col gap-2.5">
          {fields.map(({ label, placeholder, icon }) => (
            <div key={label} className="flex flex-col gap-1">
              <label className="text-[10px] text-stone-500 uppercase tracking-wider">{label}</label>
              <div className="flex items-center gap-2 rounded-xl px-3 py-3 border" style={{ background:"#252320", borderColor:"#333" }}>
                <i className={`ti ${icon} text-orange-600 text-sm`} />
                <input className="bg-transparent text-sm text-stone-300 placeholder-stone-600 outline-none flex-1" placeholder={placeholder} />
              </div>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-2.5">
            {["Status","Source"].map(l => (
              <div key={l} className="flex flex-col gap-1">
                <label className="text-[10px] text-stone-500 uppercase tracking-wider">{l}</label>
                <div className="flex items-center gap-2 rounded-xl px-3 py-3 border" style={{ background:"#252320", borderColor:"#333" }}>
                  <select className="bg-transparent text-sm text-stone-300 outline-none flex-1">
                    {(l === "Status" ? Object.keys(statusConfig) : Object.keys(sourceConfig)).map(s => (
                      <option key={s} className="bg-stone-900">{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-medium text-stone-400 border"
            style={{ background:"#252320", borderColor:"#333" }}>Cancel</button>
          <button className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98]"
            style={{ background:"#e8602a" }}>Create Lead</button>
        </div>
      </div>
    </div>
  );
}

// ─── STAT BAR ────────────────────────────────────────────────────────────────
function StatBar() {
  return (
    <>
      {/* Desktop: 4-col grid */}
      <div className="hidden md:grid grid-cols-4 gap-3 mb-5">
        {statCards.map(({ label, value, icon, color, delta }) => (
          <div key={label} className="flex items-center gap-3 rounded-2xl px-4 py-3 border"
            style={{ background:"#1a1815", borderColor:"#2a2825" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background:`${color}18`, border:`1px solid ${color}30` }}>
              <i className={`ti ${icon} text-base`} style={{ color }} />
            </div>
            <div>
              <div className="text-xl font-extrabold text-stone-100 leading-tight">{value}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="text-[10px] text-stone-500">{label}</div>
                <span className="text-[9px] font-semibold text-emerald-400">{delta}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Mobile: 2-col compact */}
      <div className="grid grid-cols-2 gap-2.5 mb-4 md:hidden">
        {statCards.map(({ label, value, icon, color, delta }) => (
          <div key={label} className="flex items-center gap-2.5 rounded-2xl px-3 py-3 border"
            style={{ background:"#1a1815", borderColor:"#2a2825" }}>
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background:`${color}18`, border:`1px solid ${color}30` }}>
              <i className={`ti ${icon} text-sm`} style={{ color }} />
            </div>
            <div className="min-w-0">
              <div className="text-base font-extrabold text-stone-100 leading-tight">{value}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <div className="text-[9px] text-stone-500 truncate">{label}</div>
                <span className="text-[8px] font-semibold text-emerald-400 flex-shrink-0">{delta}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── MOBILE FILTER BAR ───────────────────────────────────────────────────────
function MobileFilterBar({ searchQuery, setSearchQuery, activeStatus, setActiveStatus }) {
  const shortStatuses = ["All","New","Qualified","Proposal","Closed","Lost"];
  return (
    <div className="flex flex-col gap-2.5 mb-4 md:hidden">
      {/* Search */}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border"
        style={{ background:"#1a1815", borderColor:"#2a2825" }}>
        <i className="ti ti-search text-stone-600 text-sm" />
        <input className="bg-transparent text-sm text-stone-300 placeholder-stone-600 outline-none flex-1"
          placeholder="Search leads…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} className="text-stone-600">
            <i className="ti ti-x text-xs" />
          </button>
        )}
      </div>
      {/* Status pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-0.5 no-scrollbar">
        {shortStatuses.map(s => {
          const sc = statusConfig[s];
          return (
            <button key={s} onClick={() => setActiveStatus(s)}
              className="text-[10px] font-semibold rounded-full px-3 py-1.5 border flex-shrink-0 transition-all active:scale-95"
              style={activeStatus === s
                ? { background: sc ? sc.bg : "#e8602a18", color: sc ? sc.color : "#e8602a", borderColor: sc ? `${sc.color}50` : "#e8602a50" }
                : { background:"#1a1815", color:"#6b7280", borderColor:"transparent" }}>
              {s}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── LEADS CONTENT ───────────────────────────────────────────────────────────
function LeadsContent({ onShowModal }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeStatus, setActiveStatus]  = useState("All");
  const [activeSource, setActiveSource]  = useState("All Sources");
  const [searchQuery, setSearchQuery]    = useState("");
  const [viewMode, setViewMode]          = useState("table");

  const filtered = leads.filter(l => {
    const ms = activeStatus === "All" || l.status === activeStatus;
    const mc = activeSource === "All Sources" || l.source === activeSource;
    const mq = !searchQuery || [l.name, l.company, l.email].some(f => f.toLowerCase().includes(searchQuery.toLowerCase()));
    return ms && mc && mq;
  });

  const kanbanCols = ["New","Contacted","Qualified","Proposal","Closed","Lost"];

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 md:p-5">

          {/* Page header */}
          <div className="flex items-center justify-between mb-4 md:mb-5">
            <div>
              <h1 className="text-base md:text-lg font-extrabold text-stone-100 leading-tight">Leads</h1>
              <p className="text-[10px] md:text-[11px] text-stone-500 mt-0.5">Track, manage, and convert your AI-captured leads.</p>
            </div>
            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-2">
              <div className="flex rounded-xl overflow-hidden border" style={{ borderColor:"#2a2825" }}>
                {[{ id:"table", icon:"ti-table" },{ id:"kanban", icon:"ti-layout-columns" }].map(({ id, icon }) => (
                  <button key={id} onClick={() => setViewMode(id)}
                    className="w-9 h-9 flex items-center justify-center"
                    style={viewMode===id ? { background:"#e8602a22", color:"#e8602a" } : { background:"#1a1815", color:"#6b7280" }}>
                    <i className={`ti ${icon} text-sm`} />
                  </button>
                ))}
              </div>
              <button onClick={onShowModal}
                className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold text-white"
                style={{ background:"#e8602a" }}>
                <i className="ti ti-plus text-sm" /> Add Lead
              </button>
            </div>
            {/* Mobile: view toggle only */}
            <div className="flex md:hidden items-center gap-1.5">
              {[{ id:"table", icon:"ti-layout-list" },{ id:"kanban", icon:"ti-layout-columns" }].map(({ id, icon }) => (
                <button key={id} onClick={() => setViewMode(id)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all"
                  style={viewMode===id ? { background:"#e8602a22", color:"#e8602a", border:"1px solid #e8602a40" } : { background:"#1a1815", color:"#6b7280", border:"1px solid #2a2825" }}>
                  <i className={`ti ${icon} text-sm`} />
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <StatBar />

          {/* Mobile filter bar */}
          <MobileFilterBar
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            activeStatus={activeStatus} setActiveStatus={setActiveStatus}
          />

          {/* Desktop filter bar */}
          <div className="hidden md:flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ background:"#1a1815", borderColor:"#2a2825" }}>
              <i className="ti ti-search text-stone-600 text-xs" />
              <input className="bg-transparent text-[12px] text-stone-300 placeholder-stone-600 outline-none w-36"
                placeholder="Search leads…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {allStatuses.map(s => {
                const sc = statusConfig[s];
                return (
                  <button key={s} onClick={() => setActiveStatus(s)}
                    className="text-[10px] font-medium rounded-full px-3 py-1.5 border transition-all"
                    style={activeStatus===s
                      ? { background: sc?sc.bg:"#e8602a18", color: sc?sc.color:"#e8602a", borderColor: sc?`${sc.color}50`:"#e8602a50" }
                      : { background:"#1a1815", color:"#6b7280", borderColor:"transparent" }}>
                    {s}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 rounded-xl px-3 py-2 border ml-auto" style={{ background:"#1a1815", borderColor:"#2a2825" }}>
              <i className="ti ti-filter text-stone-600 text-xs" />
              <select className="bg-transparent text-[11px] text-stone-400 outline-none"
                value={activeSource} onChange={e => setActiveSource(e.target.value)}>
                {allSources.map(s => <option key={s} className="bg-stone-900">{s}</option>)}
              </select>
            </div>
          </div>

          {/* ── MOBILE: card list ── */}
          {viewMode === "table" && (
            <>
              <div className="flex flex-col gap-3 md:hidden">
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <i className="ti ti-users-off text-3xl text-stone-700" />
                    <div className="text-sm text-stone-500">No leads match your filters</div>
                  </div>
                ) : filtered.map(lead => (
                  <MobileLeadCard key={lead.id} lead={lead} onSelect={setSelectedLead} />
                ))}
              </div>

              {/* ── DESKTOP: table ── */}
              <div className="hidden md:block rounded-2xl border overflow-hidden" style={{ background:"#1a1815", borderColor:"#2a2825" }}>
                <div className="grid text-[10px] text-stone-600 uppercase tracking-wider px-4 py-3 border-b"
                  style={{ borderColor:"#2a2825", gridTemplateColumns:"2fr 1.5fr 1.2fr 0.8fr 0.8fr 0.9fr 0.7fr" }}>
                  <span>Name</span><span>Contact</span><span>Source</span>
                  <span>Score</span><span>Value</span><span>Status</span><span>Last Contact</span>
                </div>
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <i className="ti ti-users-off text-3xl text-stone-700" />
                    <div className="text-sm text-stone-500">No leads match your filters</div>
                  </div>
                ) : filtered.map(lead => {
                  const sc = statusConfig[lead.status];
                  const src = sourceConfig[lead.source];
                  const isSel = selectedLead?.id === lead.id;
                  return (
                    <div key={lead.id} onClick={() => setSelectedLead(isSel ? null : lead)}
                      className="grid items-center px-4 py-3 border-b cursor-pointer transition-all hover:bg-white/[0.02]"
                      style={{ borderColor:"#2a2825", gridTemplateColumns:"2fr 1.5fr 1.2fr 0.8fr 0.8fr 0.9fr 0.7fr",
                        background: isSel?"#e8602a0a":"transparent", borderLeft: isSel?"2px solid #e8602a":"2px solid transparent" }}>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                          style={{ background:lead.avatarColor }}>{lead.avatar}</div>
                        <div className="min-w-0">
                          <div className="text-[12px] font-semibold text-stone-200 truncate">{lead.name}</div>
                          <div className="text-[10px] text-stone-600 truncate">{lead.company}</div>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] text-stone-400 truncate">{lead.email}</div>
                        <div className="text-[10px] text-stone-600 truncate">{lead.phone}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className={`ti ${src.icon} text-xs`} style={{ color:src.color }} />
                        <span className="text-[11px] text-stone-400">{lead.source}</span>
                      </div>
                      <div className="pr-2"><ScoreBar score={lead.score} /></div>
                      <div className="text-[12px] font-semibold text-stone-200">{lead.value}</div>
                      <span className="text-[10px] font-semibold rounded-full px-2.5 py-1 w-fit"
                        style={{ color:sc.color, background:sc.bg }}>{lead.status}</span>
                      <div className="text-[10px] text-stone-600">{lead.lastContact}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── KANBAN ── */}
          {viewMode === "kanban" && (
            <div className="flex gap-3 overflow-x-auto pb-4">
              {kanbanCols.map(col => {
                const sc = statusConfig[col];
                const colLeads = filtered.filter(l => l.status === col);
                return (
                  <div key={col} className="flex flex-col gap-2 flex-shrink-0" style={{ width: 200 }}>
                    <div className="flex items-center justify-between px-3 py-2 rounded-xl"
                      style={{ background:"#1a1815", border:"1px solid #2a2825" }}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background:sc.color }} />
                        <span className="text-[11px] font-semibold" style={{ color:sc.color }}>{col}</span>
                      </div>
                      <span className="text-[10px] text-stone-600 rounded-full px-1.5 py-0.5"
                        style={{ background:"#252320" }}>{colLeads.length}</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {colLeads.map(lead => {
                        const src = sourceConfig[lead.source];
                        return (
                          <div key={lead.id} onClick={() => setSelectedLead(lead)}
                            className="rounded-xl p-3 border cursor-pointer transition-all active:scale-[0.98]"
                            style={{ background:"#1a1815", borderColor:"#2a2825" }}>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-[10px]"
                                style={{ background:lead.avatarColor }}>{lead.avatar}</div>
                              <div className="min-w-0">
                                <div className="text-[11px] font-semibold text-stone-200 truncate">{lead.name}</div>
                                <div className="text-[9px] text-stone-600 truncate">{lead.company}</div>
                              </div>
                            </div>
                            <ScoreBar score={lead.score} />
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-1">
                                <i className={`ti ${src.icon} text-[10px]`} style={{ color:src.color }} />
                                <span className="text-[9px] text-stone-600">{lead.source}</span>
                              </div>
                              <span className="text-[10px] font-semibold text-stone-300">{lead.value}</span>
                            </div>
                          </div>
                        );
                      })}
                      {colLeads.length === 0 && (
                        <div className="rounded-xl p-4 text-center text-[10px] text-stone-700 border border-dashed"
                          style={{ borderColor:"#2a2825" }}>No leads</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Desktop detail panel */}
      {selectedLead && (
        <div className="hidden md:flex flex-col border-l flex-shrink-0 overflow-y-auto"
          style={{ width:300, background:"#161412", borderColor:"#2a2825" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor:"#2a2825" }}>
            <span className="text-xs font-semibold text-stone-300">Lead Profile</span>
            <button onClick={() => setSelectedLead(null)}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-stone-600 hover:text-stone-300"
              style={{ background:"#252320" }}>
              <i className="ti ti-x text-xs" />
            </button>
          </div>
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg"
                style={{ background:selectedLead.avatarColor }}>{selectedLead.avatar}</div>
              <div className="text-center">
                <div className="text-sm font-bold text-stone-100">{selectedLead.name}</div>
                <div className="text-[11px] text-stone-500">{selectedLead.company}</div>
              </div>
              <span className="text-[10px] font-semibold rounded-full px-3 py-1"
                style={{ color:statusConfig[selectedLead.status].color, background:statusConfig[selectedLead.status].bg }}>
                {selectedLead.status}
              </span>
            </div>
            <div className="rounded-xl p-3 border" style={{ background:"#1a1815", borderColor:"#2a2825" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-stone-500">Lead Score</span>
                <i className="ti ti-sparkles text-orange-600 text-xs" />
              </div>
              <ScoreBar score={selectedLead.score} />
            </div>
            {[
              { icon:"ti-mail",  label:"Email",   val:selectedLead.email    },
              { icon:"ti-phone", label:"Phone",   val:selectedLead.phone    },
              { icon:"ti-currency-dollar", label:"Value", val:selectedLead.value },
              { icon:"ti-user",  label:"Assignee",val:selectedLead.assignee },
            ].map(({ icon, label, val }) => (
              <div key={label} className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 border"
                style={{ background:"#1a1815", border:"1px solid #2a2825" }}>
                <i className={`ti ${icon} text-orange-600 text-xs mt-0.5`} />
                <div className="min-w-0">
                  <div className="text-[9px] text-stone-600 uppercase tracking-wider">{label}</div>
                  <div className="text-[11px] text-stone-300 truncate">{val}</div>
                </div>
              </div>
            ))}
            <button className="w-full py-2.5 rounded-xl text-xs font-semibold text-white"
              style={{ background:"#e8602a" }}>Start Conversation</button>
            <div className="grid grid-cols-2 gap-2">
              <button className="py-2 rounded-xl text-[11px] font-medium text-stone-400 border"
                style={{ background:"#1a1815", borderColor:"#2a2825" }}>Send Email</button>
              <button className="py-2 rounded-xl text-[11px] font-medium text-stone-400 border"
                style={{ background:"#1a1815", borderColor:"#2a2825" }}>Edit Lead</button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom sheet */}
      <MobileLeadSheet lead={selectedLead} onClose={() => setSelectedLead(null)} />
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function Leads({ onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showModal, setShowModal]           = useState(false);

  return (
    <div className="flex flex-col md:flex-row h-screen w-screen overflow-hidden" style={{ background:"#111010" }}>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile menu */}
      <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <MobileTopbar
          onMenuOpen={() => setMobileMenuOpen(true)}
          onShowModal={() => setShowModal(true)}
          onLogout={onLogout}
        />
        {/* Desktop topbar */}
        <DesktopTopbar onLogout={onLogout} />

        <LeadsContent onShowModal={() => setShowModal(true)} />
      </div>

      {/* Add lead modal */}
      {showModal && <AddLeadModal onClose={() => setShowModal(false)} />}
    </div>
  );
}